import axios from "axios";

// External match score API base URL
const MATCH_SCORE_API_BASE_URL = "http://172.93.53.90:8085";

/**
 * Fetches match score from external API
 * @param {Object} userData - User data (must contain id field)
 * @param {Object} job - Job data (must contain id field)
 * @returns {Promise<Object>} Match score response with score, recommendation, and details
 */
export const getMatchScore = async (jobId, userId, includeCultureFit = false) => {
  try {
    // Validate required data
    if (!userId || !jobId) {
      throw new Error("User data and job data are required");
    }

    // Map data to new API format
    const requestBody = {
      user_id: userId,
      job_id: jobId,
      include_culture_fit: includeCultureFit,
    };

    // Make API request to external server
    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/get-match-score`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 1000000, // 10 second timeout
      }
    );

    // Return the match score data
    return {
      success: true,
      matchScore: response.data.match_score || 0,
      recommendation: response.data.recommendation || "",
      details: response.data.details || {},
    };
  } catch (error) {
    console.error("Error fetching match score:", error);

    // Handle different error types
    if (error.response) {
      // Server responded with error
      return {
        success: false,
        error: error.response.data?.message || "Failed to fetch match score",
        matchScore: null,
        recommendation: null,
        details: null,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        success: false,
        error: "Match score service is unavailable",
        matchScore: null,
        recommendation: null,
        details: null,
      };
    } else {
      // Other errors
      return {
        success: false,
        error: error.message || "An error occurred",
        matchScore: null,
        recommendation: null,
        details: null,
      };
    }
  }
};

/**
 * Analyzes a resume from a Cloudinary URL
 * @param {string} resumeUrl - Cloudinary URL of the resume to analyze
 * @returns {Promise<Object>} Resume analysis response with job family, level, scores, and recommendations
 */
export const analyzeResume = async (resumeUrl) => {
  try {
    // Validate required data
    if (!resumeUrl) {
      throw new Error("Resume URL is required");
    }

    // Make API request to external server
    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/analyze-resume`,
      { resume_url: resumeUrl },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 3000000, // 30 second timeout (analysis takes longer)
      }
    );

    // Return the analysis data
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Error analyzing resume:", error);

    // Handle different error types
    if (error.response) {
      // Server responded with error
      return {
        success: false,
        error: error.response.data?.message || "Failed to analyze resume",
        data: null,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        success: false,
        error: "Resume analysis service is unavailable",
        data: null,
      };
    } else {
      // Other errors
      return {
        success: false,
        error: error.message || "An error occurred",
        data: null,
      };
    }
  }
};

export default getMatchScore;
